import { TFunction } from 'i18next'
import { RRule } from 'rrule'
import { v4 as uuidv4 } from 'uuid'

import { getColorFromString, toLocalISOString, getDaysOfWeek } from './shared'
import { CalendarEvent, TimeslotContent } from '../context/Data'

export type WeatherCondition = 'cloudy' | 'sunny' | 'rainy' | 'snowy'
export type OccupancyLevel = 'low' | 'medium' | 'high'

export interface SmartSlotPlaylistConfig {
	startDate: Date
	endDate: Date
	startTime: string
	endTime: string
	repeatDays: string[]
	customEndDate: boolean
	endDateCustom?: Date
	playlists: Array<{ id: number; percentage: number }>
	volume: number
	playlistNames: string[]
	condition: WeatherCondition | OccupancyLevel
	variant: 'weather' | 'occupation'
}

export interface SmartSlotGroupConfig {
	id?: string
	name: string // e.g., "Slot 1"
	startDate: Date
	endDate: Date
	startTime: string
	endTime: string
	repeatDays: string[]
	customEndDate: boolean
	endDateCustom?: Date
	variant: 'weather' | 'occupation'
	playlistNames?: string[] // Names of playlists from first condition
	contents: Array<{
		type: string // 'clear', 'clouds', 'rain', 'snow', 'low', 'medium', 'high'
		volume: number
		playlists: Array<{ id: number; percentage: number }>
	}>
}

/**
 * Generate CalendarEvent for a smart slot condition
 * Used by both weather and occupancy steppers
 */
export const generateSmartSlotEvent = (
	config: SmartSlotPlaylistConfig,
	t: TFunction
): CalendarEvent => {
	const playlistIds = config.playlists
		.map((p) => p.id.toString())
		.sort()
		.join('|')

	const color = getColorFromString(playlistIds || `Smart ${config.condition}`)
	const slotTitle =
		config.playlistNames.length > 0
			? `${config.playlistNames.join(' + ')} (${config.condition})`
			: `Smart Slot (${config.condition})`

	const [startHour, startMinute] = config.startTime.split(':').map(Number)
	const [endHour, endMinute] = config.endTime.split(':').map(Number)

	const daysOfWeek = getDaysOfWeek(t)

	const startDate = new Date(config.startDate)
	startDate.setHours(startHour, startMinute, 0, 0)

	const endDate = new Date(config.startDate)
	endDate.setHours(endHour, endMinute, 0, 0)

	if (endDate <= startDate) {
		endDate.setDate(endDate.getDate() + 1)
	}

	const durationMs = endDate.getTime() - startDate.getTime()

	const newEvent: CalendarEvent & {
		start?: Date
		end?: Date
		rrule?: any
		duration?: any
		startTime?: string
	} = {
		id: uuidv4(),
		title: slotTitle,
		description:
			config.repeatDays.length > 0
				? `Repeats on: ${config.repeatDays.join(', ')}`
				: 'One-time slot',
		color: color,
		backgroundColor: color,
		playlists: config.playlists,
		repeatDays: config.repeatDays,
		volume: config.volume,
		slotType: 'smart',
		smartSlotConfig: {
			variant: config.variant,
			condition: config.condition,
		},
		start: new Date(),
		end: new Date(),
	}

	if (config.repeatDays.length > 0) {
		let rruleDays: any[] = []
		const isEveryday = config.repeatDays.includes('everyday')

		if (isEveryday) {
			rruleDays = [
				(RRule as any).MO,
				(RRule as any).TU,
				(RRule as any).WE,
				(RRule as any).TH,
				(RRule as any).FR,
				(RRule as any).SA,
				(RRule as any).SU,
			]
		} else {
			rruleDays = config.repeatDays
				.map((day) => daysOfWeek.find((d) => d.value === day)?.rruleDay)
				.filter(Boolean)
		}

		// Don't use toISOString() to avoid timezone conversion
		// Store dtstart as ISO string but FullCalendar will handle it as local time
		newEvent.rrule = {
			freq: (RRule as any).DAILY,
			dtstart: toLocalISOString(startDate),
		}
		// Only add byweekday if not everyday
		if (!isEveryday) {
			newEvent.rrule.byweekday = rruleDays.map((day: any) => day.weekday)
		}
		// Add until if customEndDate is set
		if (config.customEndDate && config.endDateCustom) {
			const untilDate = new Date(config.endDateCustom)
			// Set until to 23:59:59 of that day to include the entire day
			untilDate.setHours(23, 59, 59, 999)
			newEvent.rrule.until = toLocalISOString(untilDate)
		}
		newEvent.duration = { milliseconds: durationMs }
		newEvent.startTime = `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}:00`
	} else {
		newEvent.start = startDate
		newEvent.end = endDate
	}

	return newEvent as CalendarEvent
}

/**
 * Generate a single CalendarEvent for a complete smart slot with all conditions/levels
 * This creates ONE event that contains all timeslot_contents
 */
export const generateSmartSlotEventGroup = (
	config: SmartSlotGroupConfig,
	t: TFunction
): CalendarEvent => {
	const daysOfWeek = getDaysOfWeek(t)

	// Use first playlist name if available, otherwise use the slot name
	const displayName =
		config.playlistNames && config.playlistNames.length > 0
			? config.playlistNames[0]
			: config.name

	const color = getColorFromString(displayName)

	// Generate gradient colors: use first two playlist names
	let gradientColors: string[] = []
	if (config.playlistNames && config.playlistNames.length >= 2) {
		// Use first two playlist colors
		gradientColors = config.playlistNames
			.slice(0, 2)
			.map((name) => getColorFromString(name))
	} else if (config.playlistNames && config.playlistNames.length === 1) {
		// If only one playlist, create two different colors based on the same name
		const baseColor = getColorFromString(config.playlistNames[0])
		// Get a second color by slightly modifying the string
		const secondColor = getColorFromString(config.playlistNames[0] + '_alt')
		gradientColors = [baseColor, secondColor]
	} else {
		// No playlists - use default colors
		gradientColors = [color, getColorFromString(config.name + '_alt')]
	}

	const [startHour, startMinute] = config.startTime.split(':').map(Number)
	const [endHour, endMinute] = config.endTime.split(':').map(Number)

	const startDate = new Date(config.startDate)
	startDate.setHours(startHour, startMinute, 0, 0)

	const endDate = new Date(config.startDate)
	endDate.setHours(endHour, endMinute, 0, 0)

	if (endDate <= startDate) {
		endDate.setDate(endDate.getDate() + 1)
	}

	const durationMs = endDate.getTime() - startDate.getTime()

	const newEvent: CalendarEvent = {
		id: config?.id || uuidv4(),
		title: `${displayName} - Smart`,
		description:
			config.repeatDays.length > 0
				? `Smart ${config.variant} slot. Repeats on: ${config.repeatDays.join(', ')}`
				: `Smart ${config.variant} slot. One-time event`,
		color: color,
		backgroundColor: color,
		gradientColors: gradientColors,
		repeatDays: config.repeatDays,
		slotType: 'smart',
		smartSlotConfig: {
			variant: config.variant,
			condition: config.variant, // Will be used to identify smart slots
		},
		timeslotName: config.name,
		timeslotContents: config.contents as TimeslotContent[],
		start: startDate,
		end: endDate,
	}

	if (config.repeatDays.length > 0) {
		let rruleDays: any[] = []
		const isEveryday = config.repeatDays.includes('everyday')

		if (isEveryday) {
			rruleDays = [
				(RRule as any).MO,
				(RRule as any).TU,
				(RRule as any).WE,
				(RRule as any).TH,
				(RRule as any).FR,
				(RRule as any).SA,
				(RRule as any).SU,
			]
		} else {
			rruleDays = config.repeatDays
				.map((day) => daysOfWeek.find((d) => d.value === day)?.rruleDay)
				.filter(Boolean)
		}

		// Don't use toISOString() to avoid timezone conversion
		// Store dtstart as ISO string but FullCalendar will handle it as local time
		newEvent.rrule = {
			freq: (RRule as any).DAILY,
			dtstart: toLocalISOString(startDate),
		}
		// Only add byweekday if not everyday
		if (!isEveryday) {
			newEvent.rrule.byweekday = rruleDays.map((day: any) => day.weekday)
		}
		// Add until if customEndDate is set
		if (config.customEndDate && config.endDateCustom) {
			const untilDate = new Date(config.endDateCustom)
			// Set until to 23:59:59 of that day to include the entire day
			untilDate.setHours(23, 59, 59, 999)
			newEvent.rrule.until = toLocalISOString(untilDate)
		}
		newEvent.duration = { milliseconds: durationMs }
		newEvent.startTime = `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}:00`
	} else {
		newEvent.start = startDate
		newEvent.end = endDate
	}

	return newEvent
}

/**
 * Get all weather conditions
 */
export const getAllWeatherConditions = (): WeatherCondition[] => {
	return ['cloudy', 'sunny', 'rainy', 'snowy']
}

/**
 * Get all occupancy levels
 */
export const getAllOccupancyLevels = (): OccupancyLevel[] => {
	return ['low', 'medium', 'high']
}
